import { type Entity } from "@firecms/core";
import { type Tag } from "@one-ai/shared/cms/schema";

import { type CmsLocale } from "../i18n/locales";

/**
 * What to call a tag in the CMS, given the chrome locale the editor is in.
 *
 * Shared by the two places that name a tag outside its own form: the chips in a
 * news article's `TagChipsField`, and the save-time refusal in
 * `lib/retiredTags.ts`. Both have to agree — an editor who reads "活動" on a
 * chip and "3" in the error message cannot act on the error.
 */

/**
 * The content locale to name a tag in, for a CMS chrome locale.
 *
 * Content has two locales and the CMS chrome has three, so `en` has no label of
 * its own to fall back on. It gets 繁體中文, which is this CMS's default locale
 * and the one an English-reading editor is most likely to have seen in the
 * picker.
 */
export const contentLocaleFor = (locale: CmsLocale): "zh" | "jp" =>
    locale === "ja" ? "jp" : "zh";

/**
 * A tag's display name, falling back until it has something to show.
 *
 * The chain matters for a half-written tag, which is a real state —
 * `NewTagAction` writes the record before the editor fills it in. An empty
 * label falls through to the other locale, and a tag with neither falls through
 * to its id, which is the slug an editor can still recognise.
 */
export const tagDisplayName = (
    tag: Entity<Partial<Tag>>,
    locale: CmsLocale,
): string => {
    const preferred = contentLocaleFor(locale);
    const other = preferred === "zh" ? "jp" : "zh";
    const label = tag.values?.label;
    return label?.[preferred] || label?.[other] || tag.id;
};
